import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'

export type SprintReportPageState = {
  aggregateField: AggregateField
  workloadUnit: WorkloadUnit
  rowHeight: number
}

export type SprintReportPageStateApi = SprintReportPageState & {
  initialized: boolean
  setAggregateField: (value: AggregateField) => void
  setWorkloadUnit: (value: WorkloadUnit) => void
  setRowHeight: (value: number) => void
}

const DEFAULT_STATE: SprintReportPageState = {
  aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
  workloadUnit: WorkloadUnit.HOUR,
  rowHeight: ROW_HEIGHT.SMALL,
}

export const usePageState = (
  functionUuid: string
): SprintReportPageStateApi => {
  const [aggregateField, setAggregateField] = useState<AggregateField>(
    DEFAULT_STATE.aggregateField
  )
  const [workloadUnit, setWorkloadUnit] = useState<WorkloadUnit>(
    DEFAULT_STATE.workloadUnit
  )
  const [rowHeight, setRowHeight] = useState<number>(DEFAULT_STATE.rowHeight)
  const [initialized, setInitialized] = useState(false)

  // 初回マウント時に uistate から復元
  useEffect(() => {
    const restore = async () => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
        })
        const stored = response.json
        if (stored?.value) {
          const payload: Partial<SprintReportPageState> = JSON.parse(
            stored.value
          )
          if (payload.aggregateField !== undefined) {
            setAggregateField(payload.aggregateField)
          }
          if (payload.workloadUnit !== undefined) {
            setWorkloadUnit(payload.workloadUnit)
          }
          if (payload.rowHeight !== undefined) {
            setRowHeight(payload.rowHeight)
          }
        }
      } finally {
        setInitialized(true)
      }
    }
    restore()
  }, [functionUuid])

  // 変更時に uistate に保存
  useEffect(() => {
    if (!initialized) return
    uiStates.update(
      {
        key: UiStateKey.PageState,
        scope: UiStateScope.User,
        value: JSON.stringify({ aggregateField, workloadUnit, rowHeight }),
      },
      functionUuid
    )
  }, [initialized, aggregateField, workloadUnit, rowHeight, functionUuid])

  const updateAggregateField = useCallback((value: AggregateField) => {
    setAggregateField(value)
  }, [])

  const updateWorkloadUnit = useCallback((value: WorkloadUnit) => {
    setWorkloadUnit(value)
  }, [])

  const updateRowHeight = useCallback((value: number) => {
    setRowHeight(value)
  }, [])

  return useMemo(
    () => ({
      initialized,
      aggregateField,
      workloadUnit,
      rowHeight,
      setAggregateField: updateAggregateField,
      setWorkloadUnit: updateWorkloadUnit,
      setRowHeight: updateRowHeight,
    }),
    [
      initialized,
      aggregateField,
      workloadUnit,
      rowHeight,
      updateAggregateField,
      updateWorkloadUnit,
      updateRowHeight,
    ]
  )
}
